<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import MessageAPI from '../../../../api/inbox/message';
import PipelinesAPI from 'dashboard/api/pipelines';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import { emitter } from 'shared/helpers/mitt';
import Button from '../../../../components-next/button/Button.vue';
import ProgressBar from '../../../../components-next/progress/ProgressBar.vue';
import Select from '../../../../components-next/select/Select.vue';
import {
  buildMessageIdempotencyKey,
  createMessageBatchId,
  fingerprintMessageContent,
} from '../helpers/messageIdempotency';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
});
const emit = defineEmits(['close']);
const store = useStore();
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const currentAccountId = computed(
  () =>
    store.getters.getCurrentAccountId ??
    store.getters.getCurrentAccount?.id ??
    null
);

const { t } = useI18n();

const loading = ref(false);
const sendingMessage = ref(false);
const templates = ref([]);
const selectedTemplateKey = ref('');
const buildTemplateSelectionKey = (pipelineId, stageId, templateId) =>
  JSON.stringify([
    String(pipelineId ?? ''),
    String(stageId ?? ''),
    String(templateId ?? ''),
  ]);
const selectedTemplate = computed({
  get: () =>
    templates.value.find(
      template => template.selectionKey === selectedTemplateKey.value
    ) || null,
  set: template => {
    selectedTemplateKey.value =
      typeof template === 'object'
        ? template?.selectionKey || ''
        : template || '';
  },
});
const selectedConditions = ref({
  status: '',
  priority: '',
  hasAttachments: false,
  stage: '',
  assignee: '',
  lastActivity: '',
  tags: [],
  customFields: [],
});
const pipelineCards = ref([]);
const selectedConversations = ref(new Set());
const isProcessing = ref(false);
const processedItems = ref([]);
const successCount = ref(0);
const errorCount = ref(0);
const progress = ref(0);
const showFilters = ref(false);
const templatesLoadFailed = ref(false);
const cardsLoadFailed = ref(false);
const cardsLoading = ref(false);
const cardsResolved = ref(false);
const stopRequested = ref(false);
const successfulConversationIds = ref(new Set());
const batchFingerprint = ref('');
const batchId = ref('');
const batchTotal = ref(0);
let templatesRequestId = 0;
let cardsRequestId = 0;
let accountGeneration = 0;
let sendAbortController = null;
let cardsAbortController = null;
const normalizedAccountId = () => {
  const accountId = currentAccountId.value;
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};
const captureAccountContext = () => ({
  accountId: normalizedAccountId(),
  generation: accountGeneration,
});
const accountContextIsCurrent = context =>
  context.accountId !== null &&
  context.accountId === normalizedAccountId() &&
  context.generation === accountGeneration;

// Buscar templates disponíveis
const fetchTemplates = async () => {
  templatesRequestId += 1;
  const requestId = templatesRequestId;
  const accountContext = captureAccountContext();
  const requestIsCurrent = () =>
    requestId === templatesRequestId && accountContextIsCurrent(accountContext);
  if (accountContext.accountId === null) {
    templates.value = [];
    templatesLoadFailed.value = true;
    return;
  }

  try {
    loading.value = true;
    templatesLoadFailed.value = false;
    const response = await PipelinesAPI.get(accountContext.accountId);
    if (!requestIsCurrent()) return;
    const allTemplates = [];

    response.data.forEach(pipeline => {
      Object.entries(pipeline.stages || {}).forEach(([stageId, stage]) => {
        if (stage.message_templates) {
          allTemplates.push(
            ...stage.message_templates.map(template => ({
              ...template,
              pipelineId: pipeline.id,
              stageId,
              selectionKey: buildTemplateSelectionKey(
                pipeline.id,
                stageId,
                template.id
              ),
            }))
          );
        }
      });
    });

    templates.value = allTemplates;
  } catch {
    if (requestIsCurrent()) templatesLoadFailed.value = true;
  } finally {
    if (requestIsCurrent()) loading.value = false;
  }
};

// Buscar itens do Pipeline
const fetchPipelineCards = async () => {
  cardsAbortController?.abort();
  const abortController = new AbortController();
  cardsAbortController = abortController;
  cardsRequestId += 1;
  const requestId = cardsRequestId;
  const accountContext = captureAccountContext();
  const pipelineId = selectedPipeline.value?.id;
  const requestIsCurrent = () =>
    requestId === cardsRequestId &&
    accountContextIsCurrent(accountContext) &&
    String(pipelineId ?? '') === String(selectedPipeline.value?.id ?? '');
  try {
    if (accountContext.accountId === null || !pipelineId) return;
    cardsLoadFailed.value = false;
    cardsLoading.value = true;
    cardsResolved.value = false;
    pipelineCards.value = [];

    const selectedIds = new Set(
      props.items
        .map(item => item?.id)
        .filter(id => id !== null && id !== undefined)
        .map(String)
    );
    const selectedCards = new Map();
    const seenCursors = new Set();
    let cursor = null;
    let hasMore = true;

    /* eslint-disable no-await-in-loop */
    while (hasMore && selectedCards.size < selectedIds.size) {
      const response = await PipelineCardsAPI.getCards(
        pipelineId,
        {
          limit: 500,
          ...(cursor ? { cursor } : {}),
        },
        {
          accountId: accountContext.accountId,
          signal: abortController.signal,
        }
      );
      if (!requestIsCurrent()) return;

      let responseItems = [];
      if (Array.isArray(response.data?.data)) {
        responseItems = response.data.data;
      } else if (Array.isArray(response.data)) {
        responseItems = response.data;
      }
      responseItems.forEach(item => {
        const itemId = String(item?.id ?? '');
        if (selectedIds.has(itemId)) selectedCards.set(itemId, item);
      });

      const meta = response.data?.meta || {};
      hasMore = Boolean(meta.has_more);
      if (!hasMore || selectedCards.size === selectedIds.size) break;

      const nextCursor = String(meta.next_cursor ?? '').trim();
      if (!nextCursor || seenCursors.has(nextCursor)) {
        throw new Error('Pipeline card pagination did not advance');
      }
      seenCursors.add(nextCursor);
      cursor = nextCursor;
    }
    /* eslint-enable no-await-in-loop */

    if (!requestIsCurrent()) return;
    pipelineCards.value = props.items
      .map(item => selectedCards.get(String(item?.id)))
      .filter(Boolean);
    cardsResolved.value = true;
  } catch {
    if (requestIsCurrent() && !abortController.signal.aborted) {
      cardsLoadFailed.value = true;
    }
  } finally {
    if (requestIsCurrent()) cardsLoading.value = false;
    if (cardsAbortController === abortController) {
      cardsAbortController = null;
    }
  }
};

const hasConversationId = item => {
  const conversationId = item?.item_details?.conversation_id;
  return (
    conversationId !== null &&
    conversationId !== undefined &&
    String(conversationId).trim() !== ''
  );
};

const sendableItems = computed(() =>
  pipelineCards.value.filter(item => hasConversationId(item))
);
const unsendableCount = computed(() =>
  cardsResolved.value
    ? Math.max(props.items.length - sendableItems.value.length, 0)
    : 0
);
const isLoadingData = computed(() => loading.value || cardsLoading.value);
const sendableConversationCount = computed(
  () =>
    new Set(sendableItems.value.map(item => item.item_details.conversation_id))
      .size
);

// Computed para itens filtrados com todas as condições
const filteredItems = computed(() => {
  return sendableItems.value.filter(item => {
    // Filtro por status
    if (
      selectedConditions.value.status &&
      item.item_details.status !== selectedConditions.value.status
    ) {
      return false;
    }

    // Filtro por prioridade
    if (
      selectedConditions.value.priority &&
      item.item_details.priority !== selectedConditions.value.priority
    ) {
      return false;
    }

    // Filtro por anexos
    if (selectedConditions.value.hasAttachments && !item.attachments?.length) {
      return false;
    }

    // Filtro por etapa
    if (
      selectedConditions.value.stage &&
      item.pipeline_stage !== selectedConditions.value.stage
    ) {
      return false;
    }

    // Filtro por responsável
    if (
      selectedConditions.value.assignee &&
      item.item_details.agent_id !== selectedConditions.value.assignee
    ) {
      return false;
    }

    // Filtro por última atividade
    if (selectedConditions.value.lastActivity) {
      const lastActivity = new Date(
        item.item_details.conversation?.last_activity_at
      );
      const now = new Date();
      const diffDays = Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24));

      switch (selectedConditions.value.lastActivity) {
        case 'today':
          if (diffDays > 0) return false;
          break;
        case 'week':
          if (diffDays > 7) return false;
          break;
        case 'month':
          if (diffDays > 30) return false;
          break;
        default:
          break;
      }
    }

    return true;
  });
});

const filteredConversationItems = computed(() => {
  const uniqueItems = new Map();
  filteredItems.value.forEach(item => {
    const conversationId = item?.item_details?.conversation_id;
    if (!uniqueItems.has(conversationId)) uniqueItems.set(conversationId, item);
  });
  return [...uniqueItems.values()];
});
const filteredConversationIds = computed(
  () =>
    new Set(
      filteredConversationItems.value.map(
        item => item.item_details.conversation_id
      )
    )
);
const effectiveSelectionCount = computed(
  () =>
    [...selectedConversations.value].filter(id =>
      filteredConversationIds.value.has(id)
    ).length
);
const allFilteredSelected = computed(
  () =>
    filteredConversationIds.value.size > 0 &&
    effectiveSelectionCount.value === filteredConversationIds.value.size
);
const hasActiveConditionValue = value =>
  Array.isArray(value) ? value.length > 0 : Boolean(value);

const statusOptions = computed(() => [
  { value: '', label: t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.ALL') },
  { value: 'open', label: t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.OPEN') },
  { value: 'won', label: t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.WON') },
  { value: 'lost', label: t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.LOST') },
]);
const priorityOptions = computed(() => [
  { value: '', label: t('PIPELINE.BULK_ACTIONS.PRIORITY.ALL') },
  { value: 'urgent', label: t('PIPELINE.BULK_ACTIONS.PRIORITY.URGENT') },
  { value: 'high', label: t('PIPELINE.BULK_ACTIONS.PRIORITY.HIGH') },
  { value: 'medium', label: t('PIPELINE.BULK_ACTIONS.PRIORITY.MEDIUM') },
  { value: 'low', label: t('PIPELINE.BULK_ACTIONS.PRIORITY.LOW') },
]);
const stageOptions = computed(() => [
  { value: '', label: t('PIPELINE.BULK_ACTIONS.STAGE.ALL') },
  ...Object.entries(selectedPipeline.value?.stages || {}).map(
    ([value, stage]) => ({ value, label: stage.name })
  ),
]);
const activityOptions = computed(() => [
  { value: '', label: t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.ANY') },
  { value: 'today', label: t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.TODAY') },
  { value: 'week', label: t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.WEEK') },
  { value: 'month', label: t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.MONTH') },
]);
const templateOptions = computed(() => [
  {
    value: '',
    label: t('PIPELINE.BULK_ACTIONS.MESSAGE_TEMPLATE_SELECT'),
  },
  ...templates.value.map(template => ({
    value: template.selectionKey,
    label: template.title,
  })),
]);

watch(filteredConversationIds, availableIds => {
  selectedConversations.value = new Set(
    [...selectedConversations.value].filter(id => availableIds.has(id))
  );
});

const handleToggleConversation = item => {
  const id = item?.item_details?.conversation_id;
  if (id === null || id === undefined) return;
  if (selectedConversations.value.has(id)) {
    selectedConversations.value.delete(id);
  } else {
    selectedConversations.value.add(id);
  }
};

const handleSelectAll = () => {
  if (allFilteredSelected.value) {
    selectedConversations.value.clear();
  } else {
    selectedConversations.value = new Set(
      filteredConversationItems.value.map(
        item => item.item_details.conversation_id
      )
    );
  }
};

const deliveryFingerprint = computed(() =>
  fingerprintMessageContent(
    JSON.stringify({
      template: selectedTemplate.value?.selectionKey || '',
      content: selectedTemplate.value?.content || '',
      conversations: [...selectedConversations.value]
        .map(String)
        .sort((left, right) => left.localeCompare(right)),
    })
  )
);

const resetBatch = () => {
  batchFingerprint.value = '';
  batchId.value = '';
  batchTotal.value = 0;
  successfulConversationIds.value = new Set();
  processedItems.value = [];
  successCount.value = 0;
  errorCount.value = 0;
  progress.value = 0;
};

watch(deliveryFingerprint, (fingerprint, previousFingerprint) => {
  if (fingerprint !== previousFingerprint && !sendingMessage.value)
    resetBatch();
});

const ensureBatch = () => {
  if (batchFingerprint.value !== deliveryFingerprint.value) {
    resetBatch();
    batchFingerprint.value = deliveryFingerprint.value;
    batchId.value = createMessageBatchId();
    batchTotal.value = effectiveSelectionCount.value;
  }
};

const recordResult = result => {
  const existingIndex = processedItems.value.findIndex(
    item => item.conversationId === result.conversationId
  );
  if (existingIndex >= 0) {
    processedItems.value.splice(existingIndex, 1, result);
  } else {
    processedItems.value.push(result);
  }

  successCount.value = processedItems.value.filter(
    item => item.status === 'success'
  ).length;
  errorCount.value = processedItems.value.filter(
    item => item.status === 'error'
  ).length;
  progress.value = batchTotal.value
    ? (processedItems.value.length / batchTotal.value) * 100
    : 0;
};

const handleSend = async () => {
  if (
    sendingMessage.value ||
    !selectedTemplate.value ||
    effectiveSelectionCount.value === 0
  ) {
    return;
  }
  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) return;
  const abortController = new AbortController();
  sendAbortController?.abort();
  sendAbortController = abortController;
  const requestIsCurrent = () =>
    accountContextIsCurrent(accountContext) &&
    sendAbortController === abortController;

  ensureBatch();
  const content = selectedTemplate.value.content;
  const itemsToProcess = filteredConversationItems.value.filter(item => {
    const conversationId = item?.item_details?.conversation_id;
    return (
      conversationId !== null &&
      conversationId !== undefined &&
      selectedConversations.value.has(conversationId) &&
      !successfulConversationIds.value.has(conversationId)
    );
  });
  if (!itemsToProcess.length) return;

  try {
    isProcessing.value = true;
    sendingMessage.value = true;
    stopRequested.value = false;

    // Sequential awaits intentional: WhatsApp providers rate-limit
    // bulk sends, and we want predictable progress + per-item failure
    // accounting (Promise.all would mask which one failed).
    /* eslint-disable no-restricted-syntax, no-await-in-loop, no-plusplus */
    for (const item of itemsToProcess) {
      if (stopRequested.value || !requestIsCurrent()) break;
      const conversationId = item?.item_details?.conversation_id;

      try {
        const inboxId = item.item_details?.conversation?.inbox_id || 1;
        const idempotencyKey = buildMessageIdempotencyKey({
          scope: 'pipeline-bulk',
          batchId: batchId.value,
          conversationId,
          content,
        });

        await MessageAPI.create(
          {
            conversationId,
            message: content,
            private: false,
            message_type: 'outgoing',
            inbox_id: inboxId,
          },
          {
            headers: { 'Idempotency-Key': idempotencyKey },
            signal: abortController.signal,
            accountId: accountContext.accountId,
          }
        );
        if (!requestIsCurrent()) break;

        successfulConversationIds.value.add(conversationId);
        recordResult({
          id: item.id,
          title: item.item_details?.title,
          conversationId,
          status: 'success',
        });
      } catch {
        if (!requestIsCurrent() || abortController.signal.aborted) break;
        recordResult({
          id: item.id,
          title: item.item_details?.title,
          conversationId,
          status: 'error',
          error: t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.ITEM_SEND_ERROR'),
        });
      }
    }
    /* eslint-enable no-restricted-syntax, no-await-in-loop, no-plusplus */

    if (requestIsCurrent() && !stopRequested.value) {
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.MESSAGES.PROCESS_COMPLETED', {
          success: successCount.value,
          errors: errorCount.value,
        }),
        action: { type: successCount.value > 0 ? 'success' : 'error' },
      });
    }
  } catch {
    if (!requestIsCurrent() || abortController.signal.aborted) return;
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.PROCESS_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    if (sendAbortController === abortController) {
      sendAbortController = null;
    }
    if (accountContextIsCurrent(accountContext)) sendingMessage.value = false;
  }
};

const handleClose = () => {
  if (sendingMessage.value) {
    stopRequested.value = true;
    sendAbortController?.abort();
    return;
  }

  if (isProcessing.value) {
    isProcessing.value = false;
    resetBatch();
  }
  emit('close');
};

const retryLoading = () => {
  fetchTemplates();
  fetchPipelineCards();
};

onMounted(() => {
  fetchTemplates();
  fetchPipelineCards();
});

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    accountGeneration += 1;
    templatesRequestId += 1;
    cardsRequestId += 1;
    cardsAbortController?.abort();
    cardsAbortController = null;
    stopRequested.value = true;
    sendAbortController?.abort();
    sendAbortController = null;
    sendingMessage.value = false;
    isProcessing.value = false;
    templates.value = [];
    selectedTemplate.value = null;
    pipelineCards.value = [];
    cardsResolved.value = false;
    cardsLoading.value = false;
    selectedConversations.value = new Set();
    resetBatch();
    loading.value = false;
    templatesLoadFailed.value = false;
    cardsLoadFailed.value = false;
    showFilters.value = false;
    emit('close');
  },
  { flush: 'sync' }
);

onBeforeUnmount(() => {
  accountGeneration += 1;
  templatesRequestId += 1;
  cardsRequestId += 1;
  cardsAbortController?.abort();
  cardsAbortController = null;
  stopRequested.value = true;
  sendAbortController?.abort();
  sendAbortController = null;
});
</script>

<template>
  <div class="space-y-6">
    <!-- Tela de Progresso -->
    <div v-if="isProcessing" class="space-y-6">
      <div class="text-center space-y-2">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_TITLE') }}
        </h3>
        <p class="text-body-main text-n-slate-11">
          {{
            t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_DESCRIPTION', {
              count: processedItems.length,
              total: batchTotal,
            })
          }}
        </p>
      </div>

      <!-- Barra de Progresso -->
      <div class="relative pt-1">
        <div class="flex mb-2 items-center justify-between">
          <div>
            <span
              class="text-label-small font-semibold inline-block py-1 px-2 rounded-full"
              :class="{
                'text-n-teal-11 bg-n-teal-3': progress === 100,
                'text-n-brand bg-n-alpha-2': progress < 100,
              }"
            >
              {{ Math.round(progress) }}%
            </span>
          </div>
          <div class="text-right">
            <span
              class="text-label-small font-semibold inline-block text-n-slate-12"
            >
              {{ successCount }}
              {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.SUCCESS') }} •
              {{ errorCount }}
              {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.ERROR') }}
            </span>
          </div>
        </div>
        <ProgressBar
          :value="progress"
          :color="progress === 100 ? 'teal' : 'blue'"
          :label="t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_TITLE')"
          class="mb-4"
        />
      </div>

      <!-- Lista de Resultados -->
      <div
        class="border border-n-weak rounded-lg divide-y divide-n-weak max-h-[400px] overflow-y-auto"
        aria-live="polite"
      >
        <div
          v-for="item in processedItems"
          :key="item.id"
          class="p-3 flex items-center justify-between hover:bg-n-alpha-1"
        >
          <div class="flex items-center gap-3">
            <span class="flex-shrink-0">
              <fluent-icon
                :icon="
                  item.status === 'success'
                    ? 'checkmark-circle'
                    : 'error-circle'
                "
                size="20"
                :class="
                  item.status === 'success'
                    ? 'text-n-teal-11'
                    : 'text-n-ruby-11'
                "
              />
            </span>
            <div class="flex-1">
              <p class="text-label font-medium text-n-slate-12">
                {{ item.title }}
              </p>
              <p class="text-label-small text-n-slate-11">
                {{
                  t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.CONVERSATION_ID', {
                    id: item.conversationId,
                  })
                }}
              </p>
              <p v-if="item.error" class="text-label-small text-n-ruby-11 mt-1">
                {{ item.error }}
              </p>
            </div>
          </div>
          <span
            class="text-label-small px-2 py-1 rounded-full"
            :class="
              item.status === 'success'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-ruby-3 text-n-ruby-11'
            "
          >
            {{
              item.status === 'success'
                ? t('PIPELINE.MESSAGES.SENT')
                : t('PIPELINE.MESSAGES.FAILED')
            }}
          </span>
        </div>
      </div>

      <!-- Ações -->
      <div class="flex flex-wrap justify-end gap-2">
        <Button
          v-if="!sendingMessage && successCount < batchTotal"
          variant="outline"
          color="slate"
          size="sm"
          class="min-h-11"
          @click="handleSend"
        >
          {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.RETRY') }}
        </Button>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          class="min-h-11"
          @click="handleClose"
        >
          {{
            sendingMessage
              ? t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.CANCEL_SEND')
              : t('PIPELINE.CLOSE_MODAL')
          }}
        </Button>
      </div>
    </div>

    <!-- Conteúdo Original -->
    <div v-else class="space-y-6">
      <!-- Lista de conversas e Switch de Filtros -->
      <div class="space-y-4">
        <div
          class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <h4 class="text-label font-medium text-n-slate-12">
            {{
              t('PIPELINE.BULK_ACTIONS.SELECTED_CONVERSATIONS', {
                count: effectiveSelectionCount,
                total: filteredConversationIds.size,
              })
            }}
          </h4>
          <div class="flex flex-wrap items-center gap-2 sm:gap-4">
            <label
              class="flex min-h-11 items-center gap-2 text-label text-n-slate-11"
            >
              <input
                v-model="showFilters"
                type="checkbox"
                class="size-5 rounded border-n-weak text-n-brand focus-visible:ring-2 focus-visible:ring-n-brand"
              />
              <span>{{ t('PIPELINE.BULK_ACTIONS.FILTER_ITEMS') }}</span>
            </label>
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              class="min-h-11"
              @click="handleSelectAll"
            >
              {{
                allFilteredSelected
                  ? t('PIPELINE.BULK_ACTIONS.DESELECT_ALL')
                  : t('PIPELINE.BULK_ACTIONS.SELECT_ALL')
              }}
            </Button>
          </div>
        </div>

        <!-- Bloco de Filtros -->
        <div v-if="showFilters" class="space-y-4">
          <!-- Resumo dos Filtros -->
          <div class="bg-n-alpha-1 p-4 rounded-lg space-y-2">
            <div class="flex items-center justify-between">
              <h4 class="text-label font-medium text-n-slate-12">
                {{ t('PIPELINE.BULK_ACTIONS.FILTERS_ACTIVE') }}
              </h4>
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                class="min-h-11"
                @click="
                  selectedConditions = {
                    status: '',
                    priority: '',
                    hasAttachments: false,
                    stage: '',
                    assignee: '',
                    lastActivity: '',
                    tags: [],
                    customFields: [],
                  }
                "
              >
                {{ t('PIPELINE.BULK_ACTIONS.CLEAR_FILTERS') }}
              </Button>
            </div>
            <div class="flex flex-wrap gap-2">
              <template v-for="(value, key) in selectedConditions" :key="key">
                <span
                  v-if="
                    hasActiveConditionValue(value) && typeof value !== 'boolean'
                  "
                  class="inline-flex items-center gap-1 px-2 py-1 text-label-small rounded-full bg-n-alpha-2 text-n-slate-11"
                >
                  {{ key }}: {{ value }}
                  <button
                    type="button"
                    class="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                    :aria-label="$t('PIPELINE.BULK_SEND_MESSAGE.REMOVE')"
                    @click="selectedConditions[key] = ''"
                  >
                    <fluent-icon icon="dismiss" size="14" aria-hidden="true" />
                  </button>
                </span>
                <span
                  v-else-if="value === true"
                  class="inline-flex items-center gap-1 px-2 py-1 text-label-small rounded-full bg-n-alpha-2 text-n-slate-11"
                >
                  {{ key }}
                  <button
                    type="button"
                    class="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                    :aria-label="$t('PIPELINE.BULK_SEND_MESSAGE.REMOVE')"
                    @click="selectedConditions[key] = false"
                  >
                    <fluent-icon icon="dismiss" size="14" aria-hidden="true" />
                  </button>
                </span>
              </template>
            </div>
          </div>

          <!-- Condições de Filtro -->
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h4 class="text-label font-medium text-n-slate-12">
                {{ t('PIPELINE.BULK_ACTIONS.FILTER_CONVERSATIONS') }}
              </h4>
              <span class="text-label-small text-n-slate-11">
                {{
                  t('PIPELINE.BULK_ACTIONS.FILTER_CONVERSATIONS_COUNT', {
                    count: filteredConversationIds.size,
                  })
                }}
              </span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  for="bulk-send-status"
                  class="block text-label text-n-slate-12 mb-2"
                >
                  {{ t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.TITLE') }}
                </label>
                <Select
                  v-model="selectedConditions.status"
                  input-id="bulk-send-status"
                  name="bulk_send_status"
                  class="w-full [&>select]:w-full"
                  :options="statusOptions"
                  :aria-label="t('PIPELINE.BULK_ACTIONS.ITEM_STATUS.TITLE')"
                />
              </div>

              <div>
                <label
                  for="bulk-send-priority"
                  class="block text-label text-n-slate-12 mb-2"
                >
                  {{ t('PIPELINE.BULK_ACTIONS.PRIORITY.TITLE') }}
                </label>
                <Select
                  v-model="selectedConditions.priority"
                  input-id="bulk-send-priority"
                  name="bulk_send_priority"
                  class="w-full [&>select]:w-full"
                  :options="priorityOptions"
                  :aria-label="t('PIPELINE.BULK_ACTIONS.PRIORITY.TITLE')"
                />
              </div>

              <div>
                <label
                  for="bulk-send-stage"
                  class="block text-label text-n-slate-12 mb-2"
                >
                  {{ t('PIPELINE.BULK_ACTIONS.STAGE.TITLE') }}
                </label>
                <Select
                  v-model="selectedConditions.stage"
                  input-id="bulk-send-stage"
                  name="bulk_send_stage"
                  class="w-full [&>select]:w-full"
                  :options="stageOptions"
                  :aria-label="t('PIPELINE.BULK_ACTIONS.STAGE.TITLE')"
                />
              </div>

              <div>
                <label
                  for="bulk-send-activity"
                  class="block text-label text-n-slate-12 mb-2"
                >
                  {{ t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.TITLE') }}
                </label>
                <Select
                  v-model="selectedConditions.lastActivity"
                  input-id="bulk-send-activity"
                  name="bulk_send_activity"
                  class="w-full [&>select]:w-full"
                  :options="activityOptions"
                  :aria-label="t('PIPELINE.BULK_ACTIONS.LAST_ACTIVITY.TITLE')"
                />
              </div>
            </div>

            <div class="flex items-center gap-4">
              <label class="flex items-center gap-2">
                <input
                  v-model="selectedConditions.hasAttachments"
                  type="checkbox"
                  class="rounded"
                />
                <span class="text-label text-n-slate-11">
                  {{ t('PIPELINE.BULK_ACTIONS.HAS_ATTACHMENTS') }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Lista de Conversas -->
        <div
          v-if="filteredConversationItems.length > 0"
          class="max-h-60 overflow-y-auto border border-n-weak rounded-lg divide-y divide-n-weak"
        >
          <label
            v-for="item in filteredConversationItems"
            :key="item.id"
            class="min-h-11 p-2 text-label hover:bg-n-alpha-1 flex items-center gap-2 cursor-pointer text-n-slate-12 focus-within:ring-2 focus-within:ring-inset focus-within:ring-n-brand"
          >
            <input
              type="checkbox"
              :checked="
                selectedConversations.has(item.item_details.conversation_id)
              "
              class="size-5 rounded border-n-weak text-n-brand focus-visible:ring-2 focus-visible:ring-n-brand"
              @change="handleToggleConversation(item)"
            />
            <span>
              {{ item.item_details.title }} -
              {{
                t('PIPELINE.BULK_ACTIONS.CONVERSATION_ID', {
                  id: item.item_details.conversation_id,
                })
              }}
            </span>
          </label>
        </div>
        <div v-else class="text-center text-label text-n-slate-11 py-4">
          {{ t('PIPELINE.BULK_ACTIONS.NO_CONVERSATIONS_AVAILABLE') }}
        </div>
      </div>

      <!-- Seleção de Template -->
      <div class="space-y-4">
        <h4 class="text-label font-medium text-n-slate-12">
          {{ t('PIPELINE.BULK_ACTIONS.SELECT_MESSAGE_TEMPLATE') }}
        </h4>

        <div v-if="isLoadingData" class="flex justify-center" role="status">
          <span class="loading-spinner" />
        </div>

        <div
          v-else-if="templatesLoadFailed || cardsLoadFailed"
          role="alert"
          class="rounded-lg border border-n-ruby-9 bg-n-ruby-3 p-4 text-sm text-n-ruby-11"
        >
          <p>{{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.LOAD_ERROR') }}</p>
          <Button
            class="mt-2 min-h-11"
            variant="ghost"
            color="slate"
            size="sm"
            @click="retryLoading"
          >
            {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.RETRY') }}
          </Button>
        </div>

        <div v-else class="space-y-4">
          <label for="bulk-send-template" class="sr-only">
            {{ t('PIPELINE.BULK_ACTIONS.SELECT_MESSAGE_TEMPLATE') }}
          </label>
          <Select
            v-model="selectedTemplateKey"
            input-id="bulk-send-template"
            name="bulk_send_template"
            class="w-full [&>select]:w-full"
            :options="templateOptions"
            :aria-label="t('PIPELINE.BULK_ACTIONS.SELECT_MESSAGE_TEMPLATE')"
          />

          <div v-if="selectedTemplate" class="p-4 bg-n-alpha-1 rounded-lg">
            <p class="text-body-main text-n-slate-11">
              {{ selectedTemplate.content }}
            </p>
          </div>
        </div>
      </div>

      <!-- Resumo -->
      <div class="p-4 bg-n-alpha-1 rounded-lg">
        <p class="text-body-main text-n-slate-11">
          {{
            t('PIPELINE.BULK_ACTIONS.MESSAGE_SUMMARY', {
              count: effectiveSelectionCount,
              total: sendableConversationCount,
            })
          }}
        </p>
        <p
          v-if="unsendableCount"
          role="status"
          class="mt-2 text-sm text-n-amber-11"
        >
          {{
            t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.UNSENDABLE_SKIPPED', {
              count: unsendableCount,
            })
          }}
        </p>
      </div>

      <!-- Ações -->
      <div class="flex justify-end gap-2">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          class="min-h-11"
          :disabled="sendingMessage"
          @click="handleClose"
        >
          {{ $t('PIPELINE.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          class="min-h-11"
          :is-loading="sendingMessage"
          :disabled="
            sendingMessage || !selectedTemplate || effectiveSelectionCount === 0
          "
          @click="handleSend"
        >
          {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGES') }}
        </Button>
      </div>
    </div>
  </div>
</template>
